// PATH: src/components/documents/ChapterRelectureTab.tsx
import * as React from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Paper,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ReportGmailerrorredIcon from "@mui/icons-material/ReportGmailerrorred";

import type { DocumentReview, ReviewStatus } from "../../models/domain";
import { useDocumentsApi } from "../../api/documents/documents";
import { StatusPill, errorMessage, useToast } from "../ui";
import {
  REVIEW_LABELS,
  REVIEW_TONES,
  formatReviewActor,
  formatReviewEvent,
  reviewDecisionError,
} from "../chapters/reviewStatus";

interface Props {
  documentId: string;
  /** L'état a changé (décision enregistrée, ou état relu) : la liste suit. */
  onStatusChange?: (status: ReviewStatus) => void;
}

const dateFmt = new Intl.DateTimeFormat("fr-FR", { dateStyle: "medium", timeStyle: "short" });
const formatDate = (iso?: string | null) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : dateFmt.format(d);
};

/** Texte du chapitre : titres `#` / `##` mis en valeur, le reste tel quel. */
const ChapterText: React.FC<{ markdown: string }> = ({ markdown }) => {
  const blocks = React.useMemo(() => markdown.split(/\n{2,}/).map((b) => b.trim()).filter(Boolean), [markdown]);
  return (
    <Stack spacing={1.25}>
      {blocks.map((b, i) => {
        const h = /^(#{1,6})\s+([\s\S]*)$/.exec(b);
        if (h) {
          return (
            <Typography
              key={i}
              dir="auto"
              variant={h[1].length === 1 ? "subtitle1" : "subtitle2"}
              sx={{ fontWeight: 700, pt: h[1].length === 1 ? 1 : 0.5 }}
            >
              {h[2]}
            </Typography>
          );
        }
        return (
          <Typography key={i} dir="auto" variant="body2" sx={{ whiteSpace: "pre-wrap", lineHeight: 1.7 }}>
            {b}
          </Typography>
        );
      })}
    </Stack>
  );
};

/**
 * Onglet « Relecture » : relecture HUMAINE du chapitre. Les points que la
 * relecture automatique a corrigés, à vérifier en priorité, à côté du texte ;
 * puis « Valider la relecture » ou « À corriger » (commentaire obligatoire),
 * et l'historique. Un chapitre à relire reste jouable.
 */
export const ChapterRelectureTab: React.FC<Props> = ({ documentId, onStatusChange }) => {
  const { getReview, postReview, getChapterMarkdown } = useDocumentsApi();
  const toast = useToast();

  const [review, setReview] = React.useState<DocumentReview | null>(null);
  const [state, setState] = React.useState<"loading" | "ok" | "error">("loading");
  const [markdown, setMarkdown] = React.useState<string | null>(null);
  const [textState, setTextState] = React.useState<"loading" | "ok" | "absent">("loading");
  const [comment, setComment] = React.useState("");
  const [commentError, setCommentError] = React.useState<string | null>(null);
  const [saving, setSaving] = React.useState<"validee" | "a_corriger" | null>(null);

  const current = React.useRef(documentId);
  current.current = documentId;
  const onStatusChangeRef = React.useRef(onStatusChange);
  onStatusChangeRef.current = onStatusChange;

  const load = React.useCallback(
    (quiet = false) => {
      const id = documentId;
      if (!quiet) setState("loading");
      getReview(id)
        .then(({ data }) => {
          if (current.current !== id) return;
          setReview(data);
          setState("ok");
          onStatusChangeRef.current?.(data.status);
        })
        .catch(() => current.current === id && setState("error"));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [documentId],
  );

  React.useEffect(() => {
    setReview(null);
    setComment("");
    setCommentError(null);
    load();
    setMarkdown(null);
    setTextState("loading");
    const id = documentId;
    getChapterMarkdown(id)
      .then((text) => {
        if (current.current !== id) return;
        setMarkdown(text);
        setTextState("ok");
      })
      .catch(() => current.current === id && setTextState("absent"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentId]);

  const decide = async (status: "validee" | "a_corriger") => {
    const err = reviewDecisionError(status, comment);
    setCommentError(err);
    if (err || !review) return;
    setSaving(status);
    try {
      const { data } = await postReview(documentId, {
        statut: status,
        commentaire: comment.trim() || undefined,
        version: review.latest_version ?? undefined,
      });
      setReview(data);
      setComment("");
      onStatusChange?.(data.status);
      toast.success(status === "validee" ? "Relecture validée." : "Chapitre marqué « à corriger ».");
    } catch (e: any) {
      if (e?.response?.status === 409) {
        toast.warning("Une nouvelle version du chapitre a été importée pendant votre relecture : relisez-la.");
        load(true);
        setTextState("loading");
        getChapterMarkdown(documentId)
          .then((text) => {
            setMarkdown(text);
            setTextState("ok");
          })
          .catch(() => setTextState("absent"));
      } else {
        toast.error(errorMessage(e, "La décision n'a pas pu être enregistrée."));
      }
    } finally {
      setSaving(null);
    }
  };

  if (state === "loading") {
    return (
      <Stack spacing={1.5}>
        <Skeleton variant="rounded" height={120} />
        <Skeleton variant="rounded" height={260} />
      </Stack>
    );
  }
  if (state === "error" || !review) {
    return (
      <Alert
        severity="error"
        action={
          <Button color="inherit" size="small" onClick={() => load()}>
            Réessayer
          </Button>
        }
      >
        Impossible de charger la relecture du chapitre.
      </Alert>
    );
  }

  const who = formatReviewActor(review.updated_by, review.updated_by_name);
  const onOlderVersion =
    review.version != null && review.latest_version != null && review.version !== review.latest_version;

  const summary = (() => {
    switch (review.status) {
      case "a_relire":
        return `Relecture humaine demandée${review.group ? ` (groupe ${review.group})` : ""}${
          review.version != null ? ` sur la version v${review.version}` : ""
        }. Vérifiez en priorité les points ci-dessous, le texte du chapitre à côté.`;
      case "validee":
        return `Relecture validée par ${who} le ${formatDate(review.updated_at)}${
          review.version != null ? ` (v${review.version})` : ""
        }.`;
      case "a_corriger":
        return `Marqué à corriger par ${who} le ${formatDate(review.updated_at)}${
          review.version != null ? ` (v${review.version})` : ""
        }.`;
      default:
        return "Ce chapitre ne demande pas de relecture humaine. Vous pouvez tout de même la valider.";
    }
  })();

  return (
    <Stack spacing={2}>
      <Paper variant="outlined" sx={{ p: 2.5 }}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
          <Typography variant="subtitle1">Relecture</Typography>
          <StatusPill tone={REVIEW_TONES[review.status]}>{REVIEW_LABELS[review.status]}</StatusPill>
          {review.latest_version != null && (
            <StatusPill tone="neutral" dot={false} title="Dernière version importée">
              v{review.latest_version}
            </StatusPill>
          )}
        </Stack>
        <Typography variant="body2" color="text.secondary">
          {summary}
        </Typography>
        {review.source_status && (
          <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }} dir="auto">
            Statut transmis par la rédaction : {review.source_status}
          </Typography>
        )}
        {review.comment && review.status !== "a_relire" && (
          <Alert severity={review.status === "a_corriger" ? "error" : "success"} icon={false} sx={{ mt: 1.5 }}>
            <Typography variant="body2" dir="auto" sx={{ whiteSpace: "pre-wrap" }}>
              {review.comment}
            </Typography>
          </Alert>
        )}
        {onOlderVersion && (
          <Alert severity="info" sx={{ mt: 1.5 }}>
            La décision porte sur la version v{review.version} ; la v{review.latest_version}, importée depuis, a
            exactement le même texte.
          </Alert>
        )}

        <Divider sx={{ my: 2 }} />

        <TextField
          label="Commentaire"
          placeholder="Ce qui est à corriger (obligatoire pour « À corriger »), ou une remarque."
          value={comment}
          onChange={(e) => {
            setComment(e.target.value);
            if (commentError) setCommentError(null);
          }}
          error={Boolean(commentError)}
          helperText={commentError ?? "Obligatoire pour « À corriger »."}
          fullWidth
          multiline
          minRows={2}
          inputProps={{ dir: "auto", maxLength: 4000 }}
        />
        <Stack direction="row" spacing={1} justifyContent="flex-end" flexWrap="wrap" useFlexGap sx={{ mt: 1.5 }}>
          <Button
            variant="outlined"
            color="error"
            startIcon={saving === "a_corriger" ? <CircularProgress size={14} /> : <ReportGmailerrorredIcon />}
            onClick={() => void decide("a_corriger")}
            disabled={Boolean(saving)}
          >
            À corriger
          </Button>
          <Button
            variant="contained"
            color="success"
            startIcon={saving === "validee" ? <CircularProgress size={14} color="inherit" /> : <CheckCircleOutlineIcon />}
            onClick={() => void decide("validee")}
            disabled={Boolean(saving)}
          >
            Valider la relecture
          </Button>
        </Stack>
      </Paper>

      <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: { xs: "1fr", md: "minmax(0, 2fr) minmax(0, 3fr)" } }}>
        <Paper variant="outlined" sx={{ p: 2.5, alignSelf: "start" }}>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            Points à vérifier{review.points.length ? ` (${review.points.length})` : ""}
          </Typography>
          <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1.5 }}>
            Corrections faites par la relecture automatique : à vérifier en priorité.
          </Typography>
          {review.points.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              Aucun point signalé.
            </Typography>
          ) : (
            <Box component="ol" sx={{ m: 0, pl: 2.5, display: "grid", gap: 1.25 }}>
              {review.points.map((p, i) => (
                <Typography key={i} component="li" variant="body2" dir="auto" sx={{ lineHeight: 1.6 }}>
                  {p}
                </Typography>
              ))}
            </Box>
          )}
        </Paper>

        <Paper variant="outlined" sx={{ p: 2.5, minWidth: 0 }}>
          <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
            Texte du chapitre{review.latest_version != null ? ` (v${review.latest_version})` : ""}
          </Typography>
          <Box sx={{ maxHeight: "60vh", overflowY: "auto", pr: 1 }}>
            {textState === "loading" ? (
              <Stack spacing={1}>
                <Skeleton variant="text" width="50%" />
                <Skeleton variant="rounded" height={120} />
              </Stack>
            ) : textState === "absent" || markdown == null ? (
              <Typography variant="body2" color="text.secondary">
                Le texte de ce chapitre est indisponible (document qui n'a pas été importé comme chapitre).
              </Typography>
            ) : (
              <ChapterText markdown={markdown} />
            )}
          </Box>
        </Paper>
      </Box>

      <Paper variant="outlined" sx={{ p: 2.5 }}>
        <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
          Historique
        </Typography>
        {review.history.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            Aucun changement de relecture enregistré.
          </Typography>
        ) : (
          <Stack divider={<Divider flexItem />} spacing={1.25}>
            {review.history.map((e) => (
              <Box key={e.id}>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                  <StatusPill tone={REVIEW_TONES[e.status]}>{REVIEW_LABELS[e.status]}</StatusPill>
                  <Typography variant="body2" fontWeight={600}>
                    {formatReviewEvent(e)}
                  </Typography>
                  {e.version != null && (
                    <StatusPill tone="neutral" dot={false}>
                      v{e.version}
                    </StatusPill>
                  )}
                </Stack>
                <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
                  {formatReviewActor(e.actor, e.actor_name)} · {formatDate(e.created_at)}
                </Typography>
                {e.comment && (
                  <Typography variant="body2" dir="auto" sx={{ mt: 0.5, whiteSpace: "pre-wrap" }}>
                    {e.comment}
                  </Typography>
                )}
              </Box>
            ))}
          </Stack>
        )}
      </Paper>
    </Stack>
  );
};

export default ChapterRelectureTab;
